import React, { useState, useRef, useEffect } from 'react';
import { Highlighter, Target, X, Check } from 'lucide-react';

const TextHighlighter = ({ 
  textContent, 
  onPositionSelect, 
  existingPosition = null,
  isSelectingPosition = false,
  onCancelSelection
}) => {
  const [selectedRange, setSelectedRange] = useState(existingPosition);
  const [isSelecting, setIsSelecting] = useState(isSelectingPosition);
  const textRef = useRef(null);

  useEffect(() => {
    setIsSelecting(isSelectingPosition);
  }, [isSelectingPosition]);

  useEffect(() => {
    setSelectedRange(existingPosition);
  }, [existingPosition]);

  const handleMouseUp = () => {
    if (!isSelecting) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0 || selection.toString().trim() === '') return;

    const range = selection.getRangeAt(0);
    const container = textRef.current;
    
    if (!container.contains(range.commonAncestorContainer)) return;

    // Calculate character positions within the text content
    const startOffset = getTextOffset(container, range.startContainer, range.startOffset);
    const endOffset = getTextOffset(container, range.endContainer, range.endOffset);
    
    const selectedText = textContent.substring(startOffset, endOffset);
    
    const positionData = {
      start: startOffset,
      end: endOffset,
      selectedText: selectedText.trim(),
      context: getContext(textContent, startOffset, endOffset),
      searchMethod: 'contextual', // Default to contextual for flexibility
      beforeContext: getSmartBeforeContext(textContent, startOffset),
      afterContext: getSmartAfterContext(textContent, endOffset),
      searchWindow: 100,
      allowMultipleMatches: false
    };

    setSelectedRange(positionData);
    setIsSelecting(false);
    
    // Clear the browser selection
    selection.removeAllRanges();
    
    if (onPositionSelect) {
      onPositionSelect(positionData);
    }
  };

  const getTextOffset = (container, node, offset) => {
    let textOffset = 0;
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let currentNode;
    while (currentNode = walker.nextNode()) {
      if (currentNode === node) {
        return textOffset + offset;
      }
      textOffset += currentNode.textContent.length;
    }
    return textOffset;
  };

  const getContext = (text, start, end, contextLength = 50) => {
    const beforeStart = Math.max(0, start - contextLength);
    const afterEnd = Math.min(text.length, end + contextLength);
    
    return {
      before: text.substring(beforeStart, start),
      selected: text.substring(start, end),
      after: text.substring(end, afterEnd),
      fullContext: text.substring(beforeStart, afterEnd)
    };
  };

  const getSmartBeforeContext = (text, start, maxLength = 20) => {
    // Look for a good "before" anchor - something stable that appears before the date
    const beforeText = text.substring(Math.max(0, start - 100), start);
    
    // Look for common patterns that might be good anchors
    const patterns = [
      /([A-Za-z\s]+:)\s*$/,  // "Date:", "Business Date:", etc.
      /(Statistics|Report|Audit)\s*$/i,  // Report type indicators
      /(\n[A-Za-z\s]+)\s*$/,  // Last word/phrase on previous line
      /([A-Z][a-z]+\s+[A-Z][a-z]+)\s*$/  // Capitalized words like "Nightly Statistics"
    ];
    
    for (const pattern of patterns) {
      const match = beforeText.match(pattern);
      if (match && match[1] && match[1].trim().length <= maxLength) {
        return match[1].trim();
      }
    }
    
    // Fallback: use last 10-15 characters if they contain letters
    const fallback = beforeText.slice(-15).trim();
    if (/[a-zA-Z]/.test(fallback) && fallback.length >= 3) {
      return fallback;
    }
    
    return '';
  };

  const getSmartAfterContext = (text, end, maxLength = 20) => {
    // Look for a good "after" anchor - something stable that appears after the date
    const afterText = text.substring(end, Math.min(text.length, end + 100));
    
    // Look for common patterns
    const patterns = [
      /^(\s*\n[A-Za-z\s-]+)/,  // Next line with text
      /^(\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/,  // Capitalized words
      /^(\s*[-\s]*[A-Za-z]+)/  // Words possibly with dashes
    ];
    
    for (const pattern of patterns) {
      const match = afterText.match(pattern);
      if (match && match[1] && match[1].trim().length <= maxLength) {
        return match[1].trim();
      }
    }
    
    // Fallback: use first 10-15 characters if they contain letters
    const fallback = afterText.slice(0, 15).trim();
    if (/[a-zA-Z]/.test(fallback) && fallback.length >= 3) {
      return fallback;
    }
    
    return '';
  };

  const highlightText = (text) => {
    if (!selectedRange) return text;

    const { start, end } = selectedRange;
    const before = text.substring(0, start);
    const highlighted = text.substring(start, end);
    const after = text.substring(end);

    return (
      <>
        {before}
        <span className="bg-yellow-200 dark:bg-yellow-800 px-1 py-0.5 rounded border-2 border-yellow-400 dark:border-yellow-600">
          {highlighted}
        </span>
        {after}
      </>
    );
  };

  const handleClearSelection = () => {
    setSelectedRange(null);
    setIsSelecting(false);
    if (onPositionSelect) {
      onPositionSelect(null);
    }
  };

  const handleCancelSelection = () => {
    setIsSelecting(false);
    if (onCancelSelection) {
      onCancelSelection();
    }
  };

  return (
    <div className="space-y-4">
      {/* Selection Controls */}
      <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center space-x-3">
          <Target className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
              {isSelecting ? 'Select text to extract date from' : 'Positional Date Extraction'}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-400">
              {isSelecting 
                ? 'Highlight the text that contains the date value'
                : selectedRange 
                  ? `Selected: "${selectedRange.selectedText}"`
                  : 'Click "Select Position" to highlight date text'
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isSelecting ? (
            <button
              onClick={handleCancelSelection}
              className="flex items-center px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </button>
          ) : (
            <>
              {selectedRange && (
                <button
                  onClick={handleClearSelection}
                  className="flex items-center px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/40"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </button>
              )}
              <button
                onClick={() => setIsSelecting(true)}
                className="flex items-center px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/60"
              >
                <Highlighter className="w-4 h-4 mr-1" />
                Select Position
              </button>
            </>
          )}
        </div>
      </div>

      {/* Selected Position Details */}
      {selectedRange && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start space-x-2">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                Position Selected
              </p>
              <div className="space-y-2 text-sm text-green-700 dark:text-green-400">
                <div>
                  <span className="font-medium">Selected Text:</span>
                  <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-800 rounded">
                    "{selectedRange.selectedText}"
                  </span>
                </div>
                <div>
                  <span className="font-medium">Position:</span>
                  <span className="ml-2">Characters {selectedRange.start} - {selectedRange.end}</span>
                </div>
                <div>
                  <span className="font-medium">Context:</span>
                  <div className="ml-2 p-2 bg-green-100 dark:bg-green-800 rounded text-xs font-mono">
                    ...{selectedRange.context.before}
                    <span className="bg-yellow-200 dark:bg-yellow-800 px-1">
                      {selectedRange.context.selected}
                    </span>
                    {selectedRange.context.after}...
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text Content */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center justify-between">
          <span>Extracted Text Content</span>
          {isSelecting && (
            <span className="text-blue-600 dark:text-blue-400 font-medium">
              Drag to select text
            </span>
          )}
        </div>
        <pre
          ref={textRef}
          className={`text-sm text-gray-900 dark:text-white whitespace-pre-wrap overflow-x-auto max-h-96 ${
            isSelecting ? 'cursor-text select-text' : 'select-none'
          }`}
          onMouseUp={handleMouseUp}
          style={{ userSelect: isSelecting ? 'text' : 'none' }}
        >
          {selectedRange && !isSelecting ? highlightText(textContent) : textContent}
        </pre>
      </div>
    </div>
  );
};

export default TextHighlighter; 